import { createContext, useCallback, useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { io } from "socket.io-client";
import { useNavigate, useLocation } from "react-router-dom";

export const UserDataContext = createContext(null);

const DEFAULT_SESSION_TIMEOUT_MS = 50 * 60 * 1000;
const SESSION_WARNING_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["click", "keydown", "mousemove", "scroll", "touchstart"];

export const UserDataProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [userData, setUserData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  axios.defaults.withCredentials = true;

  // FETCH CURRENT USER (/api/auth/me)
  const fetchUser = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/auth/me");

      if (data.success && data.user) {
        setUserData(data.user);
      } else {
        setUserData(null);
      }
    } catch (err) {
      setUserData(null);
    } finally {
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // LOGIN (web mode)
  const login = async ({ emailOrMemberId, password }) => {
    try {
      const { data } = await axios.post("/api/auth/login", {
        emailOrMemberId,
        password,
        from: "web",
      });

      if (data.authtoken) {
        const sessionTimeoutMs =
          data.sessionTimeoutMs || data.user?.sessionTimeoutMs || DEFAULT_SESSION_TIMEOUT_MS;
        Cookies.set("authtoken", data.authtoken, {
          expires: sessionTimeoutMs / (24 * 60 * 60 * 1000),
          sameSite: "Lax",
          secure: window.location.protocol === "https:",
          path: "/",
        });
      }

      await fetchUser();

      return data;
    } catch (err) {
      throw err;
    }
  };

  // LOGOUT
  const logOut = useCallback(async () => {
    try {
      await axios.post("/api/auth/logout");
    } catch (err) {
      console.log("Logout error:", err);
    } finally {
      Cookies.remove("authtoken", { path: "/" });
      setUserData(null);
      setNotifications([]);
      setUnreadNotificationCount(0);
      setSessionWarning(false);
      navigate("/");
    }
  }, [navigate]);

  const refreshNotifications = useCallback(async () => {
    if (!userData?.id) return;

    try {
      const { data } = await axios.get("/api/notifications?limit=10");
      if (data.success) {
        setNotifications(data.notifications || []);
        setUnreadNotificationCount(data.unreadCount || 0);
      }
    } catch (err) {
      setNotifications([]);
      setUnreadNotificationCount(0);
    }
  }, [userData?.id]);

  const markNotificationRead = useCallback(async (notificationId) => {
    if (!notificationId) return;

    try {
      const { data } = await axios.post(`/api/notifications/${notificationId}/read`);
      if (!data.success) return;

      setUnreadNotificationCount(data.unreadCount || 0);
      setNotifications((current) =>
        current.map((item) =>
          item.id === notificationId
            ? { ...item, readAt: data.notification?.readAt || new Date().toISOString() }
            : item
        )
      );
      return data;
    } catch (err) {
      // Realtime unread counts will correct this after the next successful request.
      return null;
    }
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    try {
      const { data } = await axios.post("/api/notifications/read-all");
      if (!data.success) return;
      setUnreadNotificationCount(0);
      setNotifications((current) =>
        current.map((item) => ({
          ...item,
          readAt: item.readAt || new Date().toISOString(),
        }))
      );
      return data;
    } catch (err) {
      // Keep the existing local state if the request fails.
      return null;
    }
  }, []);

  useEffect(() => {
    if (!userData?.id) {
      setNotifications([]);
      setUnreadNotificationCount(0);
      return;
    }

    refreshNotifications();
  }, [refreshNotifications, userData?.id]);

  useEffect(() => {
    if (!userData?.id) return undefined;

    const socket = io("/", {
      path: "/socket.io",
      withCredentials: true,
      transports: ["websocket", "polling"],
    });

    socket.on("notification:ready", (data) => {
      setUnreadNotificationCount(data?.unreadCount || 0);
    });

    socket.on("notification:unread-count", (data) => {
      setUnreadNotificationCount(data?.unreadCount || 0);
    });

    socket.on("notification:new", (notification) => {
      if (!notification?.id) return;
      setNotifications((current) => [
        notification,
        ...current.filter((item) => item.id !== notification.id),
      ].slice(0, 10));
      setUnreadNotificationCount((count) => count + 1);
    });

    return () => {
      socket.disconnect();
    };
  }, [userData?.id]);

  useEffect(() => {
    const activeUserId = userData?.id;
    const activeSessionTimeoutMs = userData?.sessionTimeoutMs;

    if (!activeUserId) {
      setSessionWarning(false);
      return undefined;
    }

    const timeoutMs = Number(activeSessionTimeoutMs) || DEFAULT_SESSION_TIMEOUT_MS;
    const warningDelay = Math.max(timeoutMs - SESSION_WARNING_MS, Math.floor(timeoutMs * 0.8));
    const refreshIntervalMs = Math.max(60 * 1000, Math.floor(timeoutMs / 2));
    let lastRefreshAt = Date.now();
    let warningTimer;
    let logoutTimer;

    const clearTimers = () => {
      window.clearTimeout(warningTimer);
      window.clearTimeout(logoutTimer);
    };

    const resetTimers = () => {
      clearTimers();
      setSessionWarning(false);
      if (Date.now() - lastRefreshAt >= refreshIntervalMs) {
        lastRefreshAt = Date.now();
        fetchUser().catch(() => {});
      }
      warningTimer = window.setTimeout(() => setSessionWarning(true), warningDelay);
      logoutTimer = window.setTimeout(() => {
        logOut();
      }, timeoutMs);
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, resetTimers, { passive: true });
    });
    resetTimers();

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, resetTimers);
      });
    };
  }, [fetchUser, logOut, userData?.id, userData?.sessionTimeoutMs]);

  const staySignedIn = async () => {
    setSessionWarning(false);
    await fetchUser();
  };

  // If user is logged in and visits login/register page → redirect to home
  useEffect(() => {
    if (!isInitialized) return;

    const isAuthPage =
      location.pathname === "/login" ||
      location.pathname === "/register";

    if (userData && isAuthPage) {
      navigate("/", { replace: true });
    }
  }, [isInitialized, location.pathname, navigate, userData]);

  return (
    <UserDataContext.Provider
      value={{
        userData,
        setUserData,
        isInitialized,
        notifications,
        unreadNotificationCount,
        fetchUser,
        login,
        logOut,
        markAllNotificationsRead,
        markNotificationRead,
        refreshNotifications,
      }}
    >
      {sessionWarning && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/45 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl">
            <div className="mb-4">
              <h3 className="font-cinzel text-xl font-bold text-gray-900">
                Session expiring
              </h3>
              <p className="mt-2 font-outfit text-sm text-gray-600">
                You have been inactive. Stay signed in to keep working.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={logOut}
                className="rounded-md border border-gray-300 px-4 py-2 font-outfit text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Log out
              </button>
              <button
                type="button"
                onClick={staySignedIn}
                className="rounded-md bg-[#00C3FF] px-4 py-2 font-outfit text-sm font-semibold text-white hover:bg-[#009ed0]"
              >
                Stay signed in
              </button>
            </div>
          </div>
        </div>
      )}
      {children}
    </UserDataContext.Provider>
  );
};

export default UserDataProvider;
